import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import './ProductDisplay.css';
import API_BASE_URL from '../../api';

const useQuery = () => new URLSearchParams(useLocation().search);

const fmt = (n) => `£${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SidebarSection = ({ title, open, onToggle, children }) => (
    <div className="sb-section">
      <button className="sb-section-header" onClick={onToggle}>
        <span>{title}</span>
        <i className={`fas fa-chevron-${open ? 'up' : 'down'}`} />
      </button>
      {open && <div className="sb-section-body">{children}</div>}
    </div>
);

const ProductDisplay = () => {
  const { categoryId } = useParams();
  const query = useQuery();
  const navigate = useNavigate();

  const urlQ = (query.get('q') || '').trim();
  const isGlobal = !categoryId;

  const [allProducts, setAllProducts] = useState([]);
  const [search, setSearch] = useState(urlQ);

  const [selManufacturers, setSelManufacturers] = useState([]);
  const [selModels, setSelModels] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 0]);

  const [openCategory, setOpenCategory] = useState(true);
  const [openBrand,    setOpenBrand]    = useState(true);
  const [openPrice,    setOpenPrice]    = useState(true);
  const [openModel,    setOpenModel]    = useState(true);
  const [showAllModels, setShowAllModels] = useState(false);
  const [showAllBrands, setShowAllBrands] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage]         = useState(30);
  const [showSidebar, setShowSidebar] = useState(false);

  const [categoriesTree, setCategoriesTree] = useState([]);
  const [categoriesMap,  setCategoriesMap]  = useState({});

  useEffect(() => { fetchCategories(); }, []); // eslint-disable-line

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/categories`);
      const flat = (res.data || []).filter((c) => c.enabled);
      const map = {};
      flat.forEach((c) => (map[c.id] = { ...c }));
      setCategoriesMap(map);
      setCategoriesTree(structureCategories(flat));
    } catch (err) { console.error(err); }
  };

  const structureCategories = (cats) => {
    const map = {};
    const roots = [];
    cats.forEach((c) => { map[c.id] = { ...c, children: [] }; });
    cats.forEach((c) => {
      if (c.parent_id) { const p = map[c.parent_id]; if (p) p.children.push(map[c.id]); }
      else roots.push(map[c.id]);
    });
    Object.values(map).forEach((c) => c.children?.sort((a, b) => (a.sort_order ?? 9999) - (b.sort_order ?? 9999)));
    roots.sort((a, b) => (a.sort_order ?? 9999) - (b.sort_order ?? 9999));
    return roots;
  };

  const rootCategoryId = useMemo(() => {
    const n = Number(categoryId);
    if (!n || !categoriesMap[n]) return null;
    let cur = categoriesMap[n];
    while (cur?.parent_id) cur = categoriesMap[cur.parent_id];
    return cur?.id ?? null;
  }, [categoryId, categoriesMap]);

  const rootNode = useMemo(() =>
          rootCategoryId ? categoriesTree.find((r) => r.id === rootCategoryId) ?? null : null,
      [rootCategoryId, categoriesTree]);

  // Build breadcrumb chain: array of { id, name } from root → current
  const breadcrumbs = useMemo(() => {
    if (isGlobal) return [];
    const n = Number(categoryId);
    if (!n || !categoriesMap[n]) return [];
    const chain = [];
    let cur = categoriesMap[n];
    while (cur) {
      chain.unshift({ id: cur.id, name: cur.name });
      cur = cur.parent_id ? categoriesMap[cur.parent_id] : null;
    }
    return chain;
  }, [categoryId, categoriesMap, isGlobal]);

  const subcategoryOptions = useMemo(() => {
    const out = [];
    const walk = (node, level) => {
      out.push({ id: node.id, label: `${'  '.repeat(level)}${node.name}` });
      node.children?.forEach((ch) => walk(ch, level + 1));
    };
    if (isGlobal) { categoriesTree.forEach((r) => r.children?.forEach((ch) => walk(ch, 0))); return out; }
    if (!rootNode) return [];
    rootNode.children?.forEach((ch) => walk(ch, 0));
    return out;
  }, [isGlobal, categoriesTree, rootNode]);

  useEffect(() => {
    if (isGlobal) setSearch(urlQ);
  }, [urlQ]); // eslint-disable-line

  useEffect(() => {
    fetchProducts();
    setCurrentPage(1);
    setSelManufacturers([]);
    setSelModels([]);
  }, [categoryId, urlQ, isGlobal]); // eslint-disable-line

  const fetchProducts = async () => {
    try {
      let res;
      if (isGlobal) {
        if (urlQ) {
          res = await axios.get(`${API_BASE_URL}/api/products/search`, { params: { q: urlQ } });
        } else {
          res = await axios.get(`${API_BASE_URL}/api/products`);
        }
      } else {
        res = await axios.get(`${API_BASE_URL}/api/products/category/${categoryId}`, { params: { search } });
      }
      setAllProducts(res.data || []);
    } catch (err) { console.error(err); }
  };

  // Price bounds derived from products filtered by brand+model only (not price)
  const priceMin = useMemo(() => {
    const prices = allProducts
        .filter((p) => {
          if (p.price_on_application || p.price == null) return false;
          const manuOk = selManufacturers.length === 0 || selManufacturers.includes(p.manufacturer);
          const modelOk = selModels.length === 0 || selModels.includes(p.model);
          return manuOk && modelOk;
        })
        .map((p) => Number(p.price));
    return prices.length ? Math.floor(Math.min(...prices)) : 0;
  }, [allProducts, selManufacturers, selModels]);

  const priceMax = useMemo(() => {
    const prices = allProducts
        .filter((p) => {
          if (p.price_on_application || p.price == null) return false;
          const manuOk = selManufacturers.length === 0 || selManufacturers.includes(p.manufacturer);
          const modelOk = selModels.length === 0 || selModels.includes(p.model);
          return manuOk && modelOk;
        })
        .map((p) => Number(p.price));
    return prices.length ? Math.ceil(Math.max(...prices)) : 0;
  }, [allProducts, selManufacturers, selModels]);

  // Reset price range whenever bounds change
  useEffect(() => {
    setPriceRange([priceMin, priceMax]);
  }, [priceMin, priceMax]);

  const availableManufacturers = useMemo(() => {
    const map = {};
    allProducts.forEach((p) => {
      if (!p.manufacturer) return;
      const modelOk = selModels.length === 0 || selModels.includes(p.model);
      const priceOk = p.price_on_application || p.price == null ||
          (Number(p.price) >= priceRange[0] && Number(p.price) <= priceRange[1]);
      if (modelOk && priceOk) map[p.manufacturer] = (map[p.manufacturer] || 0) + 1;
    });
    allProducts.forEach((p) => {
      if (p.manufacturer && !(p.manufacturer in map)) map[p.manufacturer] = 0;
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  }, [allProducts, selModels, priceRange]);

  const availableModels = useMemo(() => {
    const map = {};
    allProducts.forEach((p) => {
      if (!p.model) return;
      const manuOk = selManufacturers.length === 0 || selManufacturers.includes(p.manufacturer);
      const priceOk = p.price_on_application || p.price == null ||
          (Number(p.price) >= priceRange[0] && Number(p.price) <= priceRange[1]);
      if (manuOk && priceOk) map[p.model] = (map[p.model] || 0) + 1;
    });
    allProducts.forEach((p) => {
      if (p.model && !(p.model in map)) map[p.model] = 0;
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  }, [allProducts, selManufacturers, priceRange]);

  const filteredProducts = useMemo(() => {
    return allProducts.filter((p) => {
      if (selManufacturers.length > 0 && !selManufacturers.includes(p.manufacturer)) return false;
      if (selModels.length > 0 && !selModels.includes(p.model)) return false;
      if (!p.price_on_application && p.price != null) {
        const price = Number(p.price);
        if (price < priceRange[0] || price > priceRange[1]) return false;
      }
      return true;
    });
  }, [allProducts, selManufacturers, selModels, priceRange]);

  const totalProducts = filteredProducts.length;
  const totalPages = perPage === 'all' ? 1 : Math.ceil(totalProducts / perPage);
  const paginated = perPage === 'all'
      ? filteredProducts
      : filteredProducts.slice((currentPage - 1) * perPage, currentPage * perPage);

  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [];
    const start = Math.max(2, currentPage - 2);
    const end   = Math.min(totalPages - 1, currentPage + 2);
    pages.push(1);
    if (start > 2) pages.push('…');
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 1) pages.push('…');
    pages.push(totalPages);
    return pages;
  }, [totalPages, currentPage]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    if (isGlobal) navigate(`/search?q=${encodeURIComponent(search.trim())}`);
    else fetchProducts();
  };

  const handleRootCategoryChange = (e) => {
    const id = e.target.value;
    setSelManufacturers([]);
    setSelModels([]);
    setCurrentPage(1);
    if (id) navigate(`/category/${id}`);
    else navigate('/search');
  };

  const toggleManufacturer = (manu) => {
    setSelManufacturers((prev) =>
        prev.includes(manu) ? prev.filter((m) => m !== manu) : [...prev, manu]
    );
    setCurrentPage(1);
  };

  const toggleModel = (model) => {
    setSelModels((prev) =>
        prev.includes(model) ? prev.filter((m) => m !== model) : [...prev, model]
    );
    setCurrentPage(1);
  };

  const clearAll = () => {
    setSelManufacturers([]);
    setSelModels([]);
    // priceRange resets automatically via the useEffect on priceMin/priceMax
    setCurrentPage(1);
  };

  const hasFilters = selManufacturers.length > 0 || selModels.length > 0 || priceRange[0] > priceMin || priceRange[1] < priceMax;

  const handleSliderLow = (e) => {
    const v = Number(e.target.value);
    if (v <= priceRange[1]) { setPriceRange([v, priceRange[1]]); setCurrentPage(1); }
  };
  const handleSliderHigh = (e) => {
    const v = Number(e.target.value);
    if (v >= priceRange[0]) { setPriceRange([priceRange[0], v]); setCurrentPage(1); }
  };

  const pageTitle = isGlobal ? (urlQ ? `Search: "${urlQ}"` : 'Search') : (rootNode?.name || 'Products'); // eslint-disable-line
  const brandList = showAllBrands ? availableManufacturers : availableManufacturers.slice(0, 5);
  const modelList = showAllModels ? availableModels : availableModels.slice(0, 5);

  return (
      <div className="pd-outer">

        {/* BREADCRUMB */}
        {(breadcrumbs.length > 0 || isGlobal) && (
            <nav className="pd-breadcrumb">
              <Link to="/" className="pd-bc-link">Home</Link>
              {isGlobal ? (
                  <>
                    <span className="pd-bc-sep">/</span>
                    <span className="pd-bc-current">{urlQ ? `Search results for "${urlQ}"` : 'All Products'}</span>
                  </>
              ) : (
                  breadcrumbs.map((crumb, i) => (
                      <React.Fragment key={crumb.id}>
                        <span className="pd-bc-sep">/</span>
                        {i === breadcrumbs.length - 1 ? (
                            <span className="pd-bc-current">{crumb.name}</span>
                        ) : (
                            <Link to={`/category/${crumb.id}`} className="pd-bc-link">{crumb.name}</Link>
                        )}
                      </React.Fragment>
                  ))
              )}
            </nav>
        )}

        {/* TOP BAR */}
        <div className="pd-topbar">

          {/* Unified pill: category dropdown + search */}
          <div className="pd-topbar-filter-group">
            <i className="fas fa-filter pd-search-icon" style={{ marginLeft: 12 }} />
            <select className="pd-root-select pd-root-select-icon" value={rootCategoryId || ''} onChange={handleRootCategoryChange}>
              <option value="">All Categories</option>
              {categoriesTree.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>

            <div className="pd-topbar-filter-divider" />

            <form className="pd-search-form" onSubmit={handleSearchSubmit}>
              <i className="fas fa-search pd-search-icon" />
              <input
                  type="text"
                  placeholder="Search products, model, stock no…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
              />
              <button type="submit"><i className="fas fa-search" /></button>
            </form>
          </div>

          {/* Mobile filter toggle */}
          <button className="pd-filter-toggle" onClick={() => setShowSidebar((v) => !v)}>
            <i className="fas fa-sliders-h" /> Filters
          </button>

          {/* Count + per-page — right side */}
          <div className="pd-top-right">
            <span className="pd-count">{totalProducts} {totalProducts === 1 ? 'product' : 'products'}</span>
            <div className="pd-perpage">
              <label>Show</label>
              <select value={perPage} onChange={(e) => { setPerPage(e.target.value === 'all' ? 'all' : Number(e.target.value)); setCurrentPage(1); }}>
                <option value={30}>30</option>
                <option value={45}>45</option>
                <option value={60}>60</option>
                <option value="all">All</option>
              </select>
            </div>
          </div>
        </div>

        {/* LAYOUT */}
        <div className="pd-layout">

          {/* SIDEBAR */}
          {showSidebar && <div className="pd-sidebar-overlay" onClick={() => setShowSidebar(false)} />}
          <aside className={`pd-sidebar${showSidebar ? ' pd-sidebar-open' : ''}`}>
            <div className="pd-sidebar-mobile-header">
              <span>Filters</span>
              <button onClick={() => setShowSidebar(false)}><i className="fas fa-times" /></button>
            </div>
            <div className="sb-header">
              <span className="sb-title">Filter by</span>
              {hasFilters && <button className="sb-clear" onClick={clearAll}>Clear all</button>}
            </div>

            {subcategoryOptions.length > 0 && (
                <SidebarSection title="Category" open={openCategory} onToggle={() => setOpenCategory((v) => !v)}>
                  <ul className="sb-cat-list">
                    {rootCategoryId && (
                        <li>
                          <button
                              className={`sb-cat-btn sb-cat-btn-viewall${!categoryId || Number(categoryId) === rootCategoryId ? ' active' : ''}`}
                              onClick={() => navigate(`/category/${rootCategoryId}`)}
                          >
                            View All
                          </button>
                        </li>
                    )}
                    {subcategoryOptions.map((c) => (
                        <li key={c.id}>
                          <button
                              className={`sb-cat-btn${Number(categoryId) === c.id ? ' active' : ''}`}
                              onClick={() => navigate(`/category/${c.id}`)}
                          >
                            {c.label}
                          </button>
                        </li>
                    ))}
                  </ul>
                </SidebarSection>
            )}

            {priceMax > 0 && (
                <SidebarSection title="Price Range" open={openPrice} onToggle={() => setOpenPrice((v) => !v)}>
                  <div className="sb-price-vals">
                    <span>{fmt(priceRange[0])}</span>
                    <span>{fmt(priceRange[1])}</span>
                  </div>
                  <div className="sb-slider-wrap">
                    <input type="range" className="sb-slider sb-slider-low"
                           min={priceMin} max={priceMax} step={Math.max(1, Math.floor((priceMax - priceMin) / 200))}
                           value={priceRange[0]} onChange={handleSliderLow} />
                    <input type="range" className="sb-slider sb-slider-high"
                           min={priceMin} max={priceMax} step={Math.max(1, Math.floor((priceMax - priceMin) / 200))}
                           value={priceRange[1]} onChange={handleSliderHigh} />
                  </div>
                </SidebarSection>
            )}

            {availableManufacturers.length > 0 && (
                <SidebarSection title="Brand" open={openBrand} onToggle={() => setOpenBrand((v) => !v)}>
                  <ul className="sb-check-list">
                    {brandList.map(([manu, count]) => (
                        <li key={manu}>
                          <label className={`sb-check-label${count === 0 ? ' sb-zero' : ''}`}>
                            <input type="checkbox"
                                   checked={selManufacturers.includes(manu)}
                                   onChange={() => toggleManufacturer(manu)} />
                            <span>{manu} <em>({count})</em></span>
                          </label>
                        </li>
                    ))}
                  </ul>
                  {availableManufacturers.length > 5 && (
                      <button className="sb-show-more" onClick={() => setShowAllBrands((v) => !v)}>
                        {showAllBrands ? 'Show less' : `Show all ${availableManufacturers.length}`}
                      </button>
                  )}
                </SidebarSection>
            )}

            {availableModels.length > 0 && (
                <SidebarSection title="Model" open={openModel} onToggle={() => setOpenModel((v) => !v)}>
                  <ul className="sb-check-list">
                    {modelList.map(([model, count]) => (
                        <li key={model}>
                          <label className={`sb-check-label${count === 0 ? ' sb-zero' : ''}`}>
                            <input type="checkbox"
                                   checked={selModels.includes(model)}
                                   onChange={() => toggleModel(model)} />
                            <span>{model} <em>({count})</em></span>
                          </label>
                        </li>
                    ))}
                  </ul>
                  {availableModels.length > 5 && (
                      <button className="sb-show-more" onClick={() => setShowAllModels((v) => !v)}>
                        {showAllModels ? 'Show less' : `Show all ${availableModels.length}`}
                      </button>
                  )}
                </SidebarSection>
            )}
          </aside>

          {/* PRODUCT GRID */}
          <div className="pd-main">
            <div className="product-list">
              {paginated.map((product) => (
                  <div key={product.id} className="product-card">
                    <Link to={`/product/${product.id}`} className="product-link">
                      {product.display_image ? (
                          <div className="product-image-container">
                            <img src={product.display_image} alt={product.name} className="product-image" />
                            <i className="bx bx-search" />
                          </div>
                      ) : product.files?.length > 0 ? (
                          <div className="product-image-container">
                            <img src={product.files[0].url} alt={product.name} className="product-image" />
                            <i className="bx bx-search" />
                          </div>
                      ) : (
                          <div className="product-image-container no-image-wrap">
                            <img src="/images/defaultimg.png" alt="" className="product-image" />
                            <span className="no-image-label">Awaiting Image</span>
                          </div>
                      )}
                      <div className="product-card-body">
                        <h2>{product.name}</h2>
                        {(product.manufacturer || product.model || product.year || product.colour || product.color) && (
                            <p className="product-meta">
                              {[product.manufacturer, product.model, product.year, product.colour || product.color]
                                  .filter(Boolean).join(' • ')}
                            </p>
                        )}
                        {product.price_on_application ? (
                            <p className="product-price product-poa">
                              <i className="fas fa-phone-alt" style={{ marginRight: 5, fontSize: '0.85rem' }} /> POA
                            </p>
                        ) : product.price != null && (
                            <p className="product-price">£{Number(product.price).toLocaleString()}</p>
                        )}
                      </div>
                    </Link>
                  </div>
              ))}
            </div>

            {filteredProducts.length === 0 && (
                <p className="pd-no-results">No products found.</p>
            )}

            {totalPages > 1 && (
                <div className="pagination">
                  <button className="page-btn" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>‹ Prev</button>
                  {pageNumbers.map((p, i) =>
                      p === '…'
                          ? <span key={`e${i}`} className="page-ellipsis">…</span>
                          : <button key={p} className={`page-btn${currentPage === p ? ' page-btn-active' : ''}`} onClick={() => setCurrentPage(p)}>{p}</button>
                  )}
                  <button className="page-btn" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next ›</button>
                </div>
            )}
          </div>
        </div>
      </div>
  );
};

export default ProductDisplay;